/**
 * CustomPhoneNumber Component
 *
 * A reusable form field component for phone number input. It is used throughout the application wherever a phone number field is needed.
 *
 * Features:
 * - Allows for phone number input with automatic country code selection and flag display.
 * - Can be customized to include a required field indicator.
 * - Handles validation for phone numbers (if required), ensuring the phone number is valid and includes the area code.
 * - Default country is set to Australia (`au`).
 *
 * Props:
 * - `isRequired` (optional): Boolean that determines if the phone number field is required (default: false).
 * - `name` (optional): The name of the field (default: 'phone').
 * - `label` (optional): The label for the input field (default: 'Phone Number').
 *
 * Validation:
 * - If `isRequired` is set to `true`, the phone number must be entered and must have at least 10 digits.
 * - If the phone number is invalid or too short, an error message will be shown.
 *
 * Usage Example:
 * <CustomPhoneNumber isRequired={true} name="userPhone" label="User Phone Number" />
 *
 */
import React, { useState } from "react";
import { Form } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

interface PhoneNumberType {
  isRequired?: boolean;
  name?: string;
  label?: string;
}

const CustomPhoneNumber = ({
  isRequired = false,
  name = "phone",
  label = "Phone Number",
}: PhoneNumberType) => {
  const [phone, setPhone] = useState<string>("+1");

  return (
    <div style={{ maxWidth: "100%" }} className="date-time-col mb-[20px]">
      <Form.Item
        name={name}
        rules={[
          {
            required: isRequired,
            message: "Phone number is required!",
          },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve(); // already handled by required
              }
              const digitsOnly = value.replace(/\D/g, "");
              if (digitsOnly.length < 11) {
                return Promise.reject(new Error("Invalid phone number!"));
              }
              return Promise.resolve();
            },
          },
        ]}
        getValueFromEvent={(value) => value} // ✅ This connects PhoneInput value to Form
      >
        <PhoneInput
          country="us"
          onlyCountries={["us"]}
          disableDropdown={true}
          value={phone} // Controlled value
          onChange={(val) => {
            // Always ensure +1 is present
            if (!val.startsWith("+1")) {
              setPhone("+1" + val.replace(/^\+?1?/, ""));
            } else {
              setPhone(val);
            }
          }}
          inputStyle={{
            width: "100%",
            height: "49px",
            borderRadius: "4px",
            border: "1px solid #d9d9d9",
            paddingLeft: "48px",
          }}
          buttonStyle={{
            borderRadius: "4px",
            border: "1px solid #d9d9d9",
          }}
        />
      </Form.Item>
    </div>
  );
};

export default CustomPhoneNumber;
